/**
 * Types for the Umbraco Content API payload
 * Endpoint: /api/v1/content?url={path}
 */

import type { UmbracoImage, UmbracoLink, UmbracoVideo } from "./layout";
// Type-only import; `product.ts` re-exports FaqBlock from here, and erased
// type imports make the cycle a non-issue at runtime.
import type { ProductVariant, StockLevel } from "./product";

/**
 * Base interface for all content blocks
 */
export interface ContentBlock {
  id: string;
  type: string;
  [key: string]: unknown;
}

/**
 * Hero Media Showcase block - split-layout hero section
 */
export interface HeroMediaShowcaseBlock extends ContentBlock {
  type: "heroMediaShowcaseBlock";
  heading: string;
  bodyText: string;
  ctaButton: UmbracoLink | null;
  backgroundImage: UmbracoImage | null;
  backgroundColor?: string | null;
  mediaType?: "image" | "video";
  backgroundVideo?: UmbracoVideo | null;
}

/**
 * Rich Text block
 */
export interface RichTextBlock extends ContentBlock {
  type: "richTextBlock";
  text: string;
}

/**
 * Image Text Overlay Card block - promotional card with background image
 */
export interface ImageTextOverlayCardBlock extends ContentBlock {
  type: "imageTextOverlayCardBlock";
  backgroundImage: UmbracoImage | null;
  backgroundImageAlt: string;
  eyebrowImage?: UmbracoImage | null;
  heading: string;
  bodyText?: string | null;
  ctaButton?: UmbracoLink | null;
  ctaLink?: UmbracoLink | null;
  overlayOpacity?: number;
  textPosition?:
    | "top-left"
    | "center-left"
    | "bottom-left"
    | "center"
    | "center-bottom";
  mediaType?: "image" | "video";
  backgroundVideo?: UmbracoVideo | null;
  /**
   * When true, the whole card links to ctaButton (or ctaLink when no button is set)
   * and the CTA is rendered as a non-interactive label
   */
  wholeCardClickable?: boolean;
  /**
   * When true, the heading is the page's main title and renders as h1
   * instead of h3 (same look, SEO only)
   */
  isMainTitle?: boolean;
}

/**
 * Image block
 */
export interface ImageBlock extends ContentBlock {
  type: "imageBlock";
  image: UmbracoImage | null;
  caption?: string;
}

/**
 * Large Image Block - full-width standalone image for visual storytelling
 * Document Type: largeImageBlock
 */
export interface LargeImageBlock extends ContentBlock {
  type: "largeImageBlock";
  /** Media type: image or video */
  mediaType?: "image" | "video";
  /** Video file (only used when mediaType is "video") */
  video?: UmbracoVideo | null;
  /** The featured image */
  image: UmbracoImage | null;
  /** Accessibility alt text (empty for decorative images) */
  imageAlt: string;
  /** Aspect ratio: auto, 16:9, 21:9, 4:3, 3:2 */
  aspectRatio?: "auto" | "16:9" | "21:9" | "4:3" | "3:2" | null;
  /** Width: full-bleed, contained, narrow */
  width?: "full-bleed" | "contained" | "narrow" | null;
  /** Optional max height in pixels */
  maxHeight?: number | null;
  /** Optional image caption */
  caption?: string | null;
  /** Border radius for bento grid use: none, small, medium, large */
  borderRadius?: "none" | "small" | "medium" | "large" | null;
  /** When true, image fills 100% of its parent grid cell */
  fitToGrid?: boolean;
}

/**
 * Bento Quote Block - title/subtitle on colored background
 * Used for stats, quotes, and breakout sections in bento grids
 */
export interface BentoQuoteBlockData extends ContentBlock {
  type: "bentoQuoteBlock";
  /** The main title, stat, or prominent text (e.g., "50K+") */
  title: string;
  /** Optional subtitle or description below the title (e.g., "Active users") */
  subtitle?: string | null;
  /** Background color (hex value) */
  backgroundColor: string;
  /** Text/font color (hex value) */
  textColor: string;
  /** Border radius: none, small, medium, large */
  borderRadius?: "none" | "small" | "medium" | "large" | null;
}

/**
 * Spacer block - empty vertical space
 */
export interface SpacerBlock extends ContentBlock {
  type: "spacerBlock";
  height?: number;
}

/**
 * Icon Text Block - content block with image/icon, heading, and body text
 * Commonly used for FAQ-style questions, feature highlights, and value propositions
 */
export interface IconTextBlockData extends ContentBlock {
  type: "iconTextBlock";
  /** Image or icon displayed above the heading */
  icon: UmbracoImage | null;
  /** Title or question */
  heading: string;
  /** Description or answer text */
  bodyText: string;
  /** Text alignment: center (default) or left */
  textAlignment?: "center" | "left";
  /** Icon size: small, medium (default), or large */
  iconSize?: "small" | "medium" | "large";
  /** Heading text color as hex (e.g., "#ffffff"). Null/undefined = default */
  headingColor?: string | null;
  /** Body text color as hex (e.g., "#c2ca97"). Null/undefined = default */
  bodyTextColor?: string | null;
}

/**
 * Product Card block - e-commerce product card with image, details, and add-to-cart
 * All content is defined directly on the block
 * Supports two variants: detailed (default) and minimal
 */
/**
 * How a product card is drawn.
 * - `detailed` — the original card: light panel, category eyebrow, description.
 * - `minimal` — no surface of its own; text sits straight on the page background.
 * - `showcase` — the category-listing card: white surface, large radius, full-width
 *   CTA on desktop and a round icon CTA on phones.
 */
export type ProductCardVariant = "detailed" | "minimal" | "showcase";

export interface ProductCardBlock extends ContentBlock {
  type: "productCardBlock";
  image: UmbracoImage | null;
  imageAlt?: string | null;
  category?: string | null;
  productName: string;
  description?: string | null;
  price?: string | null;
  /**
   * Full price breakdown (incl/excl VAT, list vs calculated), for cards from a
   * product list and CMS-authored cards alike. Used to render the B2B retailer
   * breakdown or the struck pre-discount price instead of the single `price`
   * string; null when the backend has no price for the product.
   */
  priceInfo?: ItemCardPrice | null;
  productLink?: UmbracoLink | null;
  productCardSize?: "small" | "large";
  /** Card variant — see {@link ProductCardVariant}. */
  cardVariant?: ProductCardVariant;
  /** Product reference (GUID) for add-to-cart API calls */
  productReference?: string | null;
  /**
   * The card sells a bundle. A bundle must go through the bundle endpoint, which explodes it
   * into one line per bundled item; the ordinary add-to-cart endpoint rejects it.
   */
  isBundle?: boolean | null;
  /**
   * For a bundle: buying it means picking a version for at least one bundled item, which this
   * card has no room to do — it links to the bundle page instead of adding.
   */
  bundleNeedsVariantSelection?: boolean | null;
  /**
   * Stock keeping unit. Used as `item_id` on the card's `add_to_cart` event and,
   * for B2B members, printed on the card (GROW-251 — see `useProductSku`).
   * Carried through from the source ItemCard for list cards; a CMS-authored card
   * gets it from its picked product, except for a product sold in versions
   * (null — the card does not receive the versions to apply that rule itself).
   */
  sku?: string | null;
  /**
   * Versions this product is sold in, carried through from the source ItemCard.
   * When non-empty the card asks which one to add instead of adding straight
   * away. Null/empty for CMS-authored cards and single-version products.
   */
  variants?: ProductVariant[] | null;
}

/**
 * Price info from the backend PriceViewModel
 */
export interface ItemCardPrice {
  formattedCalculatedPrice?: string | null;
  formattedCalculatedPriceWithoutVat?: string | null;
  calculatedPrice?: number | null;
  calculatedPriceWithoutVat?: number | null;
  formattedListPrice?: string | null;
  formattedListPriceWithoutVat?: string | null;
  listPrice?: number | null;
  listPriceWithoutVat?: number | null;
  currency?: string | null;
}

/**
 * Item card from the backend ItemCardViewModel
 * Used in product lists and suggested products
 */
export interface ItemCard {
  id: string;
  type: string;
  image?: UmbracoImage | null;
  header: string;
  description?: string | null;
  highlightedText?: string | null;
  link?: UmbracoLink | null;
  price?: ItemCardPrice | null;
  productCategory?: string | null;
  /** Versions this product is sold in (size, colour, ...). Empty for single-version products. */
  variants?: ProductVariant[] | null;
  sku?: string | null;
  productReference?: string | null;
  /** Bundle cards only: whether a version has to be picked for one of the bundled items. */
  bundleNeedsVariantSelection?: boolean | null;
  /**
   * ISO 8601 date the product went live. Only needed by the catalogue listing's
   * "Newest first" sort; null for CMS-authored cards and for older payloads.
   */
  publishDate?: string | null;
  /**
   * The product's availability band, for the quick order's traffic light.
   * Absent when none may be shown: stock hidden for this audience, never
   * synced, a digital product, or a product sold in versions (each version has
   * its own band, and a card chooses none — the product page shows nothing
   * until a version is picked, too).
   */
  stockLevel?: StockLevel | null;
  /**
   * Device models this product fits, as tagged in Umbraco (e.g. ["Artemis", "Luna"]).
   * Drives a listing's `?device=` filter. Omitted when the product names none.
   */
  compatibleDevices?: string[] | null;
}

/**
 * Product List Block - displays a grid of product cards with optional heading.
 * Products are resolved server-side based on the block's selection mode
 * (manual or automatic-by-category) and arrive pre-populated.
 */
export interface ProductListBlock extends ContentBlock {
  type: "productListBlock";
  /** Optional section heading */
  heading?: string | null;
  /** Resolved product list (manual or automatic, server-side) */
  products?: ItemCard[] | null;
  /** Maximum number of products to display */
  maxProducts?: number | null;
  /** Number of columns: 2, 3 (default), or 4 */
  columns?: "2" | "3" | "4" | null;
  /** Card style — see {@link ProductCardVariant}. Defaults to `minimal` here. */
  cardVariant?: ProductCardVariant;
  /** Show "View all" link */
  showViewAllLink?: boolean;
  /** Destination for "View all" link */
  viewAllLink?: UmbracoLink | null;
  /**
   * `grid` (default) is the plain curated grid this block has always rendered.
   * `catalogue` adds the category-page furniture: a result count, a sort control,
   * paged "load more" and the footer progress line.
   */
  listingMode?: "grid" | "catalogue" | null;
  /** Catalogue mode only: how many products a page shows before "Load more". */
  pageSize?: number | null;
  /**
   * How many products the category holds in total, counted before `maxProducts`
   * truncated the list. Falls back to `products.length` when absent, so the
   * footer never claims more than it can show.
   */
  totalCount?: number | null;
}

/**
 * One category pill in the CategoryHeader block — a sibling or child category,
 * with the number of products it holds.
 */
export interface CategoryChip {
  id: string;
  name: string;
  link?: UmbracoLink | null;
  /** Products in this category. Null when the block has counts switched off. */
  count?: number | null;
  /** True for the chip representing the page being viewed. */
  isActive?: boolean;
}

/**
 * Category Header Block - breadcrumbs, title, subtitle and category pills for a
 * product listing page. Everything is resolved server-side from the page it sits
 * on, so dropping the block on a category is enough to populate it.
 * Document Type: categoryHeaderBlock
 */
export interface CategoryHeaderBlock extends ContentBlock {
  type: "categoryHeaderBlock";
  /** Editor override; the backend falls back to the page name. */
  heading: string;
  /** Editor override; the backend falls back to the page's intro text (HTML). */
  subtitle?: string | null;
  /** True when `subtitle` came from the rich-text intro and needs sanitising. */
  subtitleIsHtml?: boolean;
  breadcrumbs?: UmbracoLink[] | null;
  chips?: CategoryChip[] | null;
}

/**
 * Type guard to check if a block is a CategoryHeaderBlock
 */
export function isCategoryHeaderBlock(
  block: ContentBlock
): block is CategoryHeaderBlock {
  return block.type === "categoryHeaderBlock";
}

/**
 * Block Grid item with layout information
 */
export interface BlockGridItem {
  id: string;
  type: string;
  columnSpan: number;
  rowSpan: number;
  content: ContentBlock | null;
  areas?: BlockGridArea[] | null;
}

/**
 * Block Grid area containing nested items
 */
export interface BlockGridArea {
  alias: string;
  columnSpan: number;
  rowSpan: number;
  items: BlockGridItem[];
}

/**
 * Block Grid Layout composition
 */
export interface BlockGridLayout {
  gridColumns: number;
  items: BlockGridItem[];
}

/**
 * Page compositions that can be present on a page
 */
export interface PageCompositions {
  blockGridLayout?: BlockGridLayout;
  contentBlocks?: {
    blocks: ContentBlock[];
  };
  metaData?: {
    metaTitle: string;
    metaDescription: string;
  };
  navigation?: {
    menuName: string;
    hideFromMenu: boolean;
  };
  // Add other compositions as needed
}

/**
 * Base page content response
 */
export interface PageContent {
  id: string;
  type: string;
  compositions: PageCompositions;
}

/**
 * Type guard to check if a block is a HeroMediaShowcaseBlock
 */
export function isHeroMediaShowcaseBlock(
  block: ContentBlock
): block is HeroMediaShowcaseBlock {
  return block.type === "heroMediaShowcaseBlock";
}

/**
 * Type guard to check if a block is a RichTextBlock
 */
export function isRichTextBlock(block: ContentBlock): block is RichTextBlock {
  return block.type === "richTextBlock";
}

/**
 * Type guard to check if a block is an ImageBlock
 */
export function isImageBlock(block: ContentBlock): block is ImageBlock {
  return block.type === "imageBlock";
}

/**
 * Type guard to check if a block is a LargeImageBlock
 */
export function isLargeImageBlock(
  block: ContentBlock
): block is LargeImageBlock {
  return block.type === "largeImageBlock";
}

/**
 * Type guard to check if a block is a BentoQuoteBlock
 */
export function isBentoQuoteBlock(
  block: ContentBlock
): block is BentoQuoteBlockData {
  return block.type === "bentoQuoteBlock";
}

/**
 * Type guard to check if a block is an ImageTextOverlayCardBlock
 */
export function isImageTextOverlayCardBlock(
  block: ContentBlock
): block is ImageTextOverlayCardBlock {
  return block.type === "imageTextOverlayCardBlock";
}

/**
 * Type guard to check if a block is a SpacerBlock
 */
export function isSpacerBlock(block: ContentBlock): block is SpacerBlock {
  return block.type === "spacerBlock";
}

/**
 * Type guard to check if a block is an IconTextBlock
 */
export function isIconTextBlock(
  block: ContentBlock
): block is IconTextBlockData {
  return block.type === "iconTextBlock";
}

/**
 * Type guard to check if a block is a ProductCardBlock
 */
export function isProductCardBlock(
  block: ContentBlock
): block is ProductCardBlock {
  return block.type === "productCardBlock";
}

/**
 * Type guard to check if a block is a ProductListBlock
 */
export function isProductListBlock(
  block: ContentBlock
): block is ProductListBlock {
  return block.type === "productListBlock";
}

/**
 * Type guard to check if page has block grid layout
 */
export function hasBlockGridLayout(
  compositions: PageCompositions
): compositions is PageCompositions & { blockGridLayout: BlockGridLayout } {
  return (
    compositions.blockGridLayout !== undefined &&
    compositions.blockGridLayout !== null
  );
}

/**
 * Testimonial item - individual testimonial within a TestimonialsBlock
 */
export interface TestimonialItem {
  id: string;
  /** Star rating from 1-5 */
  rating: number;
  /** The testimonial quote text */
  quote: string;
  /** Author's full name */
  authorName: string;
  /** Author's title or location (e.g., "Outfitter, Montana") */
  authorTitle?: string | null;
  /** Author's profile photo (optional) */
  authorAvatar?: UmbracoImage | null;
}

/**
 * Testimonials Block - displays customer testimonials in a grid layout
 * Document Type: testimonialsBlock
 */
export interface TestimonialsBlock extends ContentBlock {
  type: "testimonialsBlock";
  /** Optional section heading */
  heading?: string | null;
  /** Background color: cream (default), white, or gray */
  backgroundColor?: "cream" | "white" | "gray" | null;
  /** Array of testimonial items */
  testimonials: TestimonialItem[];
  /** Number of columns: 2, 3 (default), or 4 */
  columns?: "2" | "3" | "4" | null;
}

/**
 * Type guard to check if a block is a TestimonialsBlock
 */
export function isTestimonialsBlock(
  block: ContentBlock
): block is TestimonialsBlock {
  return block.type === "testimonialsBlock";
}

/**
 * Article teaser item - simplified article page from the backend
 * Each teaser item is an ArticlePageSimpleViewModel with compositions
 */
export interface ArticleTeaserItem {
  id: string;
  type: string;
  compositions: {
    navigation?: {
      link?: UmbracoLink | null;
      image?: UmbracoImage | null;
    } | null;
    contentHero?: {
      hero?: {
        media?: UmbracoImage | null;
      } | null;
    } | null;
    metaData?: {
      metaTitle?: string | null;
      metaDescription?: string | null;
    } | null;
    contentCustomPublishDate?: {
      customPublishDate?: string | null;
    } | null;
  };
}

/**
 * Newsletter Signup Block - email signup form with input, button, and disclaimer
 * Document Type: newsletterSignupBlock
 */
export interface NewsletterSignupBlock extends ContentBlock {
  type: "newsletterSignupBlock";
  placeholder: string;
  buttonText: string;
  disclaimerText: string;
}

/**
 * Article List Teaser Block - displays a teaser list of articles from a linked article listing page
 * Document Type: articleListTeaserBlock
 */
export interface ArticleListTeaserBlock extends ContentBlock {
  type: "articleListTeaserBlock";
  /** Optional section heading (falls back to article listing page name) */
  heading?: string | null;
  /** Optional link text for the article listing page link */
  linkText?: string | null;
  /** Link to the article listing page */
  link?: UmbracoLink | null;
  /** Article teaser items (max 3, fetched from the article listing page) */
  teaserItems: ArticleTeaserItem[];
}

/**
 * Type guard to check if a block is a NewsletterSignupBlock
 */
export function isNewsletterSignupBlock(
  block: ContentBlock
): block is NewsletterSignupBlock {
  return block.type === "newsletterSignupBlock";
}

/**
 * Type guard to check if a block is an ArticleListTeaserBlock
 */
export function isArticleListTeaserBlock(
  block: ContentBlock
): block is ArticleListTeaserBlock {
  return block.type === "articleListTeaserBlock";
}

/**
 * Reference to a category page used by the Articles Listing block.
 * Identifies the category for both filter pills and per-card pill labels.
 */
export interface CategoryReference {
  id: string;
  type: string;
  name: string;
  link?: UmbracoLink | null;
}

/**
 * Lightweight article item embedded in an ArticlesListingBlock payload.
 * Contains only what's needed for the listing card — the full article body is fetched on navigation.
 */
export interface ArticleListingItem {
  id: string;
  type: string;
  title: string;
  intro?: string | null;
  image?: UmbracoImage | null;
  author?: string | null;
  authorImage?: UmbracoImage | null;
  /** ISO 8601 date string */
  publishDate?: string | null;
  link?: UmbracoLink | null;
  categories: CategoryReference[];
}

/**
 * Articles Listing Block - filterable, searchable grid of articles scoped to selected categories.
 * Document Type: articlesListingBlock
 */
export interface ArticlesListingBlock extends ContentBlock {
  type: "articlesListingBlock";
  heading?: string | null;
  searchPlaceholder?: string | null;
  /** When true, the most recent article renders as a large featured card above the grid (only while "All" is active and search is empty). */
  showFeatured?: boolean;
  /** Categories shown as filter pills. When empty, no pills render and all articles are listed. */
  categories: CategoryReference[];
  /** All matching articles for client-side filter, search, and load-more */
  items: ArticleListingItem[];
  /** When true, the heading is the page's main title and renders as h1 instead of h2 (same look, SEO only) */
  isMainTitle?: boolean;
}

/**
 * Type guard to check if a block is an ArticlesListingBlock
 */
export function isArticlesListingBlock(
  block: ContentBlock
): block is ArticlesListingBlock {
  return block.type === "articlesListingBlock";
}

/**
 * Cart Items Block - displays editable cart line items (data from cart API)
 */
export interface CartItemsBlock extends ContentBlock {
  type: "cartItemsBlock";
}

/**
 * Cart Summary Block - displays order summary, promo code, checkout button (data from cart API)
 */
export interface CartSummaryBlock extends ContentBlock {
  type: "cartSummaryBlock";
}

/**
 * Type guard to check if a block is a CartItemsBlock
 */
export function isCartItemsBlock(block: ContentBlock): block is CartItemsBlock {
  return block.type === "cartItemsBlock";
}

/**
 * Type guard to check if a block is a CartSummaryBlock
 */
export function isCartSummaryBlock(
  block: ContentBlock
): block is CartSummaryBlock {
  return block.type === "cartSummaryBlock";
}

/**
 * Pricing Tier Item - an individual pricing tier within a pricing table
 */
export interface PricingTierProduct {
  /** Text shown in the product selector dropdown for this product */
  label?: string | null;
  /** Resolved URL to the product page */
  href: string;
  /** Formatted price of the product (currency-aware) */
  formattedPrice?: string | null;
  /** Product reference (GUID) */
  productReference: string;
}

export interface PricingTierItem {
  id: string;
  /** Short title displayed under the tier name (e.g., "For casual hunters") */
  title?: string | null;
  /** Name of the pricing tier (e.g., "Free", "Standard", "Premium") */
  tierName: string;
  /** Image displayed inside the tier card on mobile */
  image?: UmbracoImage | null;
  /** Price display text (e.g., "0€", "32.99€"). Optional — products override this. */
  price?: string | null;
  /** Text after the price (e.g., "per year") */
  priceSuffix?: string | null;
  /** Optional prefix above feature list (e.g., "Everything from Standard plus:") */
  featuresPrefix?: string | null;
  /** List of feature strings displayed as a checklist */
  features: string[];
  /** CTA button label */
  buttonText: string;
  /** CTA button link — when set, always wins over per-product link. */
  buttonLink?: UmbracoLink | null;
  /** Whether this tier is highlighted as the recommended option */
  isHighlighted?: boolean;
  /** Optional list of products. When non-empty, shows a product selector and the CTA targets the selected product. */
  products?: PricingTierProduct[];
}

/**
 * Pricing Table Block - displays a comparison table of pricing tiers
 * Document Type: pricingTableBlock
 */
export interface PricingTableBlock extends ContentBlock {
  type: "pricingTableBlock";
  /** Optional section heading */
  heading?: string | null;
  /** Optional subtitle below the heading */
  subtitle?: string | null;
  /** Array of pricing tier items */
  pricingTiers: PricingTierItem[];
  /** When true, the heading is the page's main title and renders as h1 instead of h2 (same look, SEO only) */
  isMainTitle?: boolean;
}

/**
 * Type guard to check if a block is a PricingTableBlock
 */
export function isPricingTableBlock(
  block: ContentBlock
): block is PricingTableBlock {
  return block.type === "pricingTableBlock";
}

/**
 * Media Gallery Item - an individual item in a media gallery/carousel
 */
export interface MediaGalleryItem {
  id: string;
  /** Media type: image or video */
  mediaType: "image" | "video";
  /** Image for image type, or thumbnail for video type */
  image?: UmbracoImage | null;
  /** Video URL for video type (YouTube, Vimeo, or direct URL) */
  videoUrl?: string | null;
  /** Optional caption */
  caption?: string | null;
}

/**
 * Media Gallery Block - displays a carousel of images and videos
 * Document Type: mediaGalleryBlock
 */
export interface MediaGalleryBlock extends ContentBlock {
  type: "mediaGalleryBlock";
  /** Optional section heading */
  heading?: string | null;
  /** Optional description text */
  description?: string | null;
  /** Array of media gallery items */
  items: MediaGalleryItem[];
}

/**
 * Type guard to check if a block is a MediaGalleryBlock
 */
export function isMediaGalleryBlock(
  block: ContentBlock
): block is MediaGalleryBlock {
  return block.type === "mediaGalleryBlock";
}

/**
 * Tabbed Feature Item - an individual tab with heading, text, and image
 */
export interface TabbedFeatureItem {
  id: string;
  /** Tab heading (e.g., "Planning for clarity") */
  heading: string;
  /** Description text */
  text: string;
  /** Image displayed when this tab is active */
  image: UmbracoImage;
}

/**
 * Tabbed Feature Block - split layout with image that changes per active tab
 * Document Type: tabbedFeatureBlock
 */
export interface TabbedFeatureBlock extends ContentBlock {
  type: "tabbedFeatureBlock";
  /** Array of feature tab items */
  tabs: TabbedFeatureItem[];
}

/**
 * Type guard to check if a block is a TabbedFeatureBlock
 */
export function isTabbedFeatureBlock(
  block: ContentBlock
): block is TabbedFeatureBlock {
  return block.type === "tabbedFeatureBlock";
}

/**
 * Product Image Gallery Block - displays product image carousel (data from ProductContext)
 */
export interface ProductImageGalleryBlock extends ContentBlock {
  type: "productImageGalleryBlock";
}

/**
 * Product Details Block - displays product info, quantity selector, benefits, accordion (data from ProductContext)
 */
export interface ProductDetailsBlock extends ContentBlock {
  type: "productDetailsBlock";
}

/**
 * Type guard to check if a block is a ProductImageGalleryBlock
 */
export function isProductImageGalleryBlock(
  block: ContentBlock
): block is ProductImageGalleryBlock {
  return block.type === "productImageGalleryBlock";
}

/**
 * Type guard to check if a block is a ProductDetailsBlock
 */
export function isProductDetailsBlock(
  block: ContentBlock
): block is ProductDetailsBlock {
  return block.type === "productDetailsBlock";
}

/**
 * Product Detail Content Block - placeholder; iterates productContent.productDetailBlocks
 * and renders the full-bleed dark-theme sections below the hero (GROW-302).
 */
export interface ProductDetailContentBlock extends ContentBlock {
  type: "productDetailContentBlock";
}

export function isProductDetailContentBlock(
  block: ContentBlock
): block is ProductDetailContentBlock {
  return block.type === "productDetailContentBlock";
}

/**
 * Single FAQ item — one question/answer row in an FAQ accordion.
 */
export interface FaqItem {
  id: string;
  question: string;
  answer: string;
}

/**
 * FAQ accordion block. Authored either as a per-product detail block or as a
 * page-level Block Grid block — the same React component renders both.
 */
export interface FaqBlock extends ContentBlock {
  type: "faqBlock";
  heading?: string | null;
  items: FaqItem[];
}

export function isFaqBlock(block: ContentBlock): block is FaqBlock {
  return block.type === "faqBlock";
}

/**
 * Instagram Feed Block - displays a grid of latest Instagram posts
 * Document Type: instagramFeedBlock
 */
export interface InstagramFeedBlock extends ContentBlock {
  type: "instagramFeedBlock";
  /** Optional heading displayed above the feed grid */
  heading?: string | null;
  /** Instagram Business Account ID used to fetch posts */
  instagramAccountId: string;
  /** Number of posts to display (default: 6) */
  postCount?: number;
}

/**
 * Type guard to check if a block is an InstagramFeedBlock
 */
export function isInstagramFeedBlock(
  block: ContentBlock
): block is InstagramFeedBlock {
  return block.type === "instagramFeedBlock";
}

// ---------------------------------------------------------------------------
// Content Carousel Block
// ---------------------------------------------------------------------------

/**
 * Base interface for all content carousel slide variants.
 * The `slideType` field acts as a discriminator for the union type.
 */
interface ContentCarouselSlideBase {
  id: string;
  slideType: string;
}

/**
 * Intro slide - full-width centered layout with logo, heading, CTA
 */
export interface ContentCarouselIntroSlide extends ContentCarouselSlideBase {
  slideType: "intro";
  /** Optional logo image displayed above the heading */
  logo?: UmbracoImage | null;
  /** Main heading text */
  heading: string;
  /** Optional subtitle / description */
  subtitle?: string | null;
  /** Optional call-to-action button */
  ctaButton?: UmbracoLink | null;
  /** Optional background image behind the centered content */
  backgroundImage?: UmbracoImage | null;
}

/**
 * Text + Image slide - 6/6 grid layout with image position control
 */
export interface ContentCarouselTextImageSlide extends ContentCarouselSlideBase {
  slideType: "textImage";
  /** Heading text */
  heading: string;
  /** Rich text body content (HTML string from Umbraco RichText) */
  bodyText: string;
  /** Slide image */
  image: UmbracoImage;
  /** Controls whether the image appears on the left or right side */
  imagePosition: "left" | "right";
  /** Optional background image behind the slide content */
  backgroundImage?: UmbracoImage | null;
}

/**
 * Full Image slide - background image with centered text overlay
 */
export interface ContentCarouselFullImageSlide extends ContentCarouselSlideBase {
  slideType: "fullImage";
  /** Optional heading text */
  heading?: string | null;
  /** Optional subtitle / description */
  subtitle?: string | null;
  /** Optional call-to-action button */
  ctaButton?: UmbracoLink | null;
  /** Full-width background image */
  backgroundImage: UmbracoImage;
}

/**
 * Discriminated union of all content carousel slide types
 */
export type ContentCarouselSlide =
  | ContentCarouselIntroSlide
  | ContentCarouselTextImageSlide
  | ContentCarouselFullImageSlide;

/**
 * Content Carousel Block - full-width carousel with varying slide layouts
 * Document Type: contentCarouselBlock
 */
export interface ContentCarouselBlock extends ContentBlock {
  type: "contentCarouselBlock";
  /** Array of slides, each with its own layout type */
  slides: ContentCarouselSlide[];
}

/**
 * Type guard to check if a block is a ContentCarouselBlock
 */
export function isContentCarouselBlock(
  block: ContentBlock
): block is ContentCarouselBlock {
  return block.type === "contentCarouselBlock";
}

/** Type guard for intro slides */
export function isIntroSlide(
  slide: ContentCarouselSlide
): slide is ContentCarouselIntroSlide {
  return slide.slideType === "intro";
}

/** Type guard for text+image slides */
export function isTextImageSlide(
  slide: ContentCarouselSlide
): slide is ContentCarouselTextImageSlide {
  return slide.slideType === "textImage";
}

/** Type guard for full-image slides */
export function isFullImageSlide(
  slide: ContentCarouselSlide
): slide is ContentCarouselFullImageSlide {
  return slide.slideType === "fullImage";
}

// ---------------------------------------------------------------------------
// Feature Comparison Block
// ---------------------------------------------------------------------------

/**
 * Feature Comparison Row Item - a single feature with availability flags
 * in the same order as the parent block's plan list.
 */
export interface FeatureComparisonRowItem {
  id: string;
  featureName: string;
  groupHeading?: string | null;
  tooltip?: string | null;
  planAvailability: boolean[];
}

/**
 * Feature Comparison Block - table comparing features across subscription plans
 * Document Type: featureComparisonBlock
 */
export interface FeatureComparisonBlock extends ContentBlock {
  type: "featureComparisonBlock";
  heading?: string | null;
  subtitle?: string | null;
  planNames: string[];
  features: FeatureComparisonRowItem[];
  footerText?: string | null;
  ctaButton?: UmbracoLink | null;
}

/**
 * Type guard to check if a block is a FeatureComparisonBlock
 */
export function isFeatureComparisonBlock(
  block: ContentBlock
): block is FeatureComparisonBlock {
  return block.type === "featureComparisonBlock";
}

// ---------------------------------------------------------------------------
// Styled Row Block (Layout Container)
// ---------------------------------------------------------------------------

/**
 * Styled Row Block - layout container with configurable visual styling.
 * Editors place content blocks inside this row's area. The row applies
 * background color, text color, padding, margin, border, and border radius
 * around all child blocks.
 * Document Type: styledRowBlock
 */
export interface StyledRowBlock extends ContentBlock {
  type: "styledRowBlock";
  /** Background color preset: cream, white, gray (default: transparent) */
  backgroundColor?: "cream" | "white" | "gray" | null;
  /** Text color preset: default, white, dark, primary */
  textColor?: "default" | "white" | "dark" | "primary" | null;
  /** Inner spacing: none, small, medium, large, xl */
  padding?: "none" | "small" | "medium" | "large" | "xl" | null;
  /** Vertical spacing above/below: none, small, medium, large */
  margin?: "none" | "small" | "medium" | "large" | null;
  /** Border thickness: none, thin, medium, thick */
  border?: "none" | "thin" | "medium" | "thick" | null;
  /** Corner rounding: none, small, medium, large */
  borderRadius?: "none" | "small" | "medium" | "large" | null;
  /** Span the viewport (background + border); content stays on the page grid */
  fullWidth?: boolean;
  /** Cancel the block grid gap above the row so stacked rows touch */
  removeGapAbove?: boolean;
}

/**
 * Type guard to check if a block is a StyledRowBlock
 */
export function isStyledRowBlock(block: ContentBlock): block is StyledRowBlock {
  return block.type === "styledRowBlock";
}
